import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@monksflow/monks-ui';
import { Comment } from './Comment';
import { CommentPill } from './CommentPill';
import { CommentAnchorChip } from './CommentAnchorChip';
import { Rows3Icon } from 'lucide-react';
import type { CommentProps } from './Comment.types';
import { useTranslation } from 'react-i18next';

export interface BriefCommentProps extends CommentProps {
  annotationCount?: number;
  highlightRemoved?: boolean;
  /**
   * Set when this comment is anchored to a whole section. The published comment names what it
   * is attached to, the same way the composer did while it was being written — the same chip,
   * minus the clear control, which only means anything while drafting.
   */
  sectionAnchorName?: string;
}

export function BriefComment({
  annotationCount = 0,
  highlightRemoved = false,
  sectionAnchorName,
  ...rest
}: BriefCommentProps) {
  const { t } = useTranslation('projects');

  const sectionPill = sectionAnchorName ? (
    <CommentAnchorChip
      key="section-anchor"
      testId="comment-section-anchor-pill"
      icon={<Rows3Icon className="size-3 shrink-0 text-foreground-subtle" />}
      label={sectionAnchorName}
      ariaLabel={t('projectComment.briefComment.commentIsAnchoredToSection', {
        section: sectionAnchorName,
      })}
    />
  ) : undefined;

  const anchorPills = highlightRemoved ? (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex shrink-0 items-center rounded-md bg-muted px-2 py-1 text-xs font-normal leading-none text-muted-foreground select-none">
            {t('projectComment.briefComment.textRemoved')}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          {t('projectComment.briefComment.textTheHighlightReferencedHas')}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ) : annotationCount > 0 ? (
    <CommentPill
      variant="warning"
      testId="comment-highlight-pill"
      ariaLabel={t('projectComment.briefComment.highlight')}
      label={t('projectComment.briefComment.highlight')}
    />
  ) : undefined;

  const pills =
    anchorPills || sectionPill ? (
      <>
        {anchorPills}
        {sectionPill}
      </>
    ) : undefined;

  return <Comment {...rest} pills={pills} />;
}
